import { TwoValueTuple } from "@/lib/types";
import React, { FC, useState } from "react";
import CustomSwitch from "../CustomSwitch";
import CustomRangeFeature from "../CustomRangeFeature";
import { Pack, updateData } from "@/lib/feature/assuranceSante/slice";
import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import { useTranslation } from "react-i18next";

type DynamicObjectType<Tuple extends TwoValueTuple<number, number>> = {
  [K in Tuple[0] | Tuple[1]]: {
    label: string;
    features: string[];
  };
};

interface IAdditionalOption {
  id: number;
  label: string;
  value: string;
  levelRange?: TwoValueTuple<number, number>;
  isRecommended?: boolean;
  features: DynamicObjectType<TwoValueTuple<number, number>> | string[];
}

interface IQuestionPackage {
  value: Pack | null;
  adhrType: "main" | "partner" | "children";
  options: Array<{
    id: number;
    principal: string;
    features: Array<{ name: string; isIncluded: boolean }>;
    isRecommendedPackage?: boolean;
  }>;
  additionalOptions?: Array<IAdditionalOption>;
  resetButton?: {
    text: string;
  };
  confirmButtonText: string;
  isSubmitAccessible: boolean;
  onReset: VoidFunction;
  onSubmit: VoidFunction;
}

export const CheckIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <g clipPath="url(#clip0_770_6143)">
        <path
          d="M12 24C18.6276 24 24 18.6276 24 12C24 5.3724 18.6276 0 12 0C5.3724 0 0 5.3724 0 12C0 18.6276 5.3724 24 12 24Z"
          fill="#3B82F6"
          fillOpacity="0.25"
        ></path>
        <path
          d="M6.57031 12.7759L9.67353 15.8791L17.4316 8.12109"
          stroke="#006D73"
          strokeWidth="1.84615"
          strokeLinecap="round"
          strokeLinejoin="round"
        ></path>
      </g>
      <defs>
        <clipPath id="clip0_770_6143">
          <rect width="24" height="24" fill="white"></rect>
        </clipPath>
      </defs>
    </svg>
  );
};

export const XIcon = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_770_6168)">
        <path
          d="M12 24C18.6276 24 24 18.6276 24 12C24 5.3724 18.6276 0 12 0C5.3724 0 0 5.3724 0 12C0 18.6276 5.3724 24 12 24Z"
          fill="#888894"
          fillOpacity="0.14"
        ></path>
        <path
          d="M7.41406 7.5L16.4141 16.5"
          stroke="#888894"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        ></path>
        <path
          d="M16.4141 7.5L7.41406 16.5"
          stroke="#888894"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        ></path>
      </g>
      <defs>
        <clipPath id="clip0_770_6168">
          <rect width="24" height="24" fill="white"></rect>
        </clipPath>
      </defs>
    </svg>
  );
};

const QuestionPackage: FC<IQuestionPackage> = ({
  value,
  adhrType,
  options,
  additionalOptions,
  resetButton,
  confirmButtonText,
  isSubmitAccessible = true,
  onReset,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const state = useAppSelector((state) => state.services.assuranceSante);

  const [isBasic, setIsBasic] = useState<boolean>(!value ? true : false);
  const [showAdditionalOptions, setShowAdditionalOptions] = useState(false);

  const handleSelectBasic = () => {
    const clone = state.data.adherent.map((adher) => {
      if (adher.type === adhrType)
        return {
          ...adher,
          pack: undefined,
        };
      else return adher;
    });
    dispatch(
      updateData({
        data: {
          item: "adherent",
          value: clone,
        },
      })
    );

    onReset();

    setIsBasic(true);
  };

  const handleSelectPrimaryOption = (
    option: IQuestionPackage["options"][0]
  ) => {
    const clone = state.data.adherent.map((adher) => {
      if (adher.type === adhrType)
        return {
          ...adher,
          pack: { id: option.id, principal: option.principal, options: [] },
        };
      else return adher;
    });
    dispatch(
      updateData({
        data: {
          item: "adherent",
          value: clone,
        },
      })
    );

    setIsBasic(false);
  };

  const handleToggleAdditionalOptionsShow = () => {
    setShowAdditionalOptions(!showAdditionalOptions);
  };

  const handleToggleAddOrRemoveAdditionalOption = (
    additionalOption: IAdditionalOption,
    isOn: boolean
  ) => {
    const targetAdhr = state.data.adherent.find(
      (adhr) => adhr.type === adhrType
    );

    if (!targetAdhr) return;

    const clone = state.data.adherent.map((adhr) => {
      if (adhr.type === adhrType) {
        if (isOn) {
          // Add option if not already present
          if (
            !adhr.pack?.options.find(
              (opt) => opt.label === additionalOption.value
            )
          ) {
            return {
              ...adhr,
              pack: {
                ...adhr.pack,
                options: [
                  ...(adhr.pack?.options || []),
                  {
                    id: additionalOption.id,
                    label: additionalOption.value,
                    level: 1,
                    features: (
                      additionalOption.features[1] as {
                        label: string;
                        features: string[];
                      }
                    ).features,
                  },
                ],
              },
            };
          }
        } else {
          // Remove option if present
          const filteredOptions =
            adhr.pack?.options.filter(
              (opt) => opt.label !== additionalOption.value
            ) || [];

          return {
            ...adhr,
            pack: {
              ...adhr.pack,
              options: filteredOptions.length ? filteredOptions : [],
            },
          };
        }
      }
      return adhr;
    });

    dispatch(
      updateData({
        data: {
          item: "adherent",
          value: clone,
        },
      })
    );
  };

  const handleUpdateTargetAdditionalOptionLevel = (
    additionalOption: IAdditionalOption,
    level: number
  ) => {
    const clone = state.data.adherent.map((adhr) => {
      if (adhr.type === adhrType) {
        return {
          ...adhr,
          pack: {
            ...adhr.pack,
            options: adhr.pack?.options.map((opt) => {
              if (opt.label === additionalOption.value) {
                return {
                  ...opt,
                  level,
                  features: (
                    additionalOption.features[level] as {
                      label: string;
                      features: string[];
                    }
                  ).features,
                };
              }
              return opt;
            }),
          },
        };
      }
      return adhr;
    });

    dispatch(
      updateData({
        data: {
          item: "adherent",
          value: clone,
        },
      })
    );
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-col items-end gap-4 md:flex-row">
        {options.map((option) => (
          <button
            key={option.id}
            className={`relative flex w-full flex-col rounded-xl border-[1.5px] ${
              value?.id === option.id
                ? "border-primary bg-[#3B82F60D]"
                : "border-grey/10 bg-white"
            } p-4 ${option.isRecommendedPackage ? "!pt-[28px]" : ""}`}
            onClick={() => handleSelectPrimaryOption(option)}
          >
            {option.isRecommendedPackage && (
              <div className="absolute left-1/2 right-1/2 top-0 grid h-[32px] w-fit -translate-x-1/2 -translate-y-1/2 place-items-center rounded-[32px] theme-bg-color px-[14px] text-white">
                <span className="text-[13px] font-semibold leading-[normal] tracking-[-0.26px]">
                  {t("AssuranceSante.package.key41")}
                </span>
              </div>
            )}
            <div className="mb-5 flex w-full items-center justify-between">
              <span className="text-[18px] font-bold leading-[normal]">
                {option.principal}
              </span>
            </div>
            <ul className="space-y-5">
              {option.features.map((feature, index) => (
                <li key={index} className="flex items-center gap-1.5 text-left">
                  <div className="flex-shrink-0">
                    {feature.isIncluded ? <CheckIcon /> : <XIcon />}
                  </div>
                  <span className="text-xs font-medium">{feature.name}</span>
                </li>
              ))}
            </ul>
          </button>
        ))}
      </div>

      {/* Additional options */}
      <div className="container-shadow mt-5 rounded-xl border-[1.5px] border-[#8888941A] bg-white px-5 py-4">
        <button
          className="flex w-full items-center justify-between text-left"
          onClick={handleToggleAdditionalOptionsShow}
        >
          <span className="text-[16px] font-medium leading-[normal]">
            {t("AssuranceSante.package.key42")}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`text-grey transition-transform ${
              showAdditionalOptions ? "rotate-180" : ""
            }`}
          >
            <path d="M6 9l6 6l6 -6"></path>
          </svg>
        </button>

        {/* Content */}
        <div
          className={
            showAdditionalOptions
              ? `grid-rows-[1fr] grid overflow-hidden transition-[grid-template-rows] duration-700 ease-out`
              : `grid-rows-[0fr] grid overflow-hidden transition-[grid-template-rows] duration-700 ease-out`
          }
        >
          <div
            className={
              showAdditionalOptions
                ? "mt-8 p-1 grid grid-cols-1 gap-8 overflow-hidden md:grid-cols-2"
                : " grid grid-cols-1 gap-8 overflow-hidden md:grid-cols-2"
            }
          >
            {additionalOptions?.map((option, index) => (
              <button
                key={option.id}
                className={`relative flex w-full flex-col rounded-xl border-[1.5px] ${
                  value?.options.find((op) => op.label === option.value)
                    ? "border-primary bg-[#3B82F60D]"
                    : "border-gray-400 border-opacity-10 bg-white"
                } p-4 ${option.isRecommended ? "!pt-[20px]" : ""}`}
                disabled={isBasic}
              >
                {option.isRecommended && (
                  <div className="absolute left-1/2 right-1/2 top-0 grid h-[32px] w-fit -translate-x-1/2 -translate-y-1/2 place-items-center rounded-[32px] theme-bg-color px-[14px] text-white">
                    <span className="text-[13px] font-semibold leading-[normal] tracking-[-0.26px]">
                      {t("AssuranceSante.package.key41")}
                    </span>
                  </div>
                )}

                <div className="mb-5 flex w-full items-center justify-between">
                  <span className="text-[18px] font-bold leading-[normal]">
                    {option.label}
                  </span>
                  <CustomSwitch
                    isOn={
                      value?.options.find((op) => op.label === option.value)
                        ? true
                        : false
                    }
                    onSwitched={(isOn) =>
                      handleToggleAddOrRemoveAdditionalOption(option, isOn)
                    }
                  />
                </div>

                {Array.isArray(option.features) ? (
                  <React.Fragment>
                    <ul className="space-y-5">
                      {option.features.map((feature, index) => (
                        <li
                          key={index}
                          className="flex items-center gap-1.5 text-left"
                        >
                          <div className="flex-shrink-0">
                            <CheckIcon />
                          </div>
                          <span className="text-xs font-medium">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </React.Fragment>
                ) : (
                  <CustomRangeFeature
                    data={{
                      levelRange: option.levelRange as [number, number],
                      levelFeatures: option.features as {
                        [key: number]: {
                          label: string;
                          features: string[];
                        };
                      },
                    }}
                    currentLevel={
                      value?.options.find((op) => op.label === option.value)
                        ?.level ?? 1
                    }
                    onSelectLevel={(level) =>
                      handleUpdateTargetAdditionalOptionLevel(option, level)
                    }
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-8 grid justify-between gap-8 md:flex md:grid-cols-2">
        <button
          type="button"
          onClick={handleSelectBasic}
          className="inline-flex items-center justify-center whitespace-nowrap rounded-[56px] text-base font-semibold ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-center border-2 border-thirdy text-dark hover:bg-thirdy/5 h-[56px] px-[26px] gap-4"
        >
          {resetButton?.text || "I only want basic insurance"}
          {isBasic && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="tabler-icon tabler-icon-check"
            >
              <path d="M5 12l5 5l10 -10"></path>
            </svg>
          )}
        </button>
        {(isSubmitAccessible || isBasic) && (
          <button
            onClick={onSubmit}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-[56px] text-base font-semibold ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-center theme-bg-color text-white hover:bg-sky-600 h-[56px] px-[26px] w-40"
          >
            {t("AssuranceSante.package.key43")}
          </button>
        )}
      </div>
    </div>
  );
};

export default QuestionPackage;
